import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Search as SearchIcon, Sort as SortIcon, Add as AddIcon } from '@mui/icons-material';
import Header from '../../components/Layout/Header';
import Sidebar from '../../components/Layout/Sidebar';
import ClassForm from '../../components/Forms/ClassForm';
import { classService, teacherService } from '../../services/apiService';
import { showToast } from '../../utils/toast';
import EditIcon from '@mui/icons-material/Edit';
import ConfirmationDialog from '../../components/Common/ConfirmationDialog';

const Classes = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleDrawerToggle = () => {
    setSidebarOpen((prev) => !prev);
  };
  const drawerWidth = 280;

  // State management
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [formError, setFormError] = useState(null);
  const [searchText, setSearchText] = useState('');
  
  // Teachers for head teacher selection
  const [teachers, setTeachers] = useState([]);
  const [teachersLoading, setTeachersLoading] = useState(false);
  const [teachersError, setTeachersError] = useState(null);
  
  // Roll number order status for each class
  const [rollOrderStatus, setRollOrderStatus] = useState({}); // { classId: { needsReorder: boolean, checking: boolean } }
  
  // Confirmation dialog state
  const [reorderConfirmDialog, setReorderConfirmDialog] = useState({
    open: false,
    classId: null,
  });

  // Fetch classes from API
  const fetchClasses = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await classService.getAll();
      
      if (response.success) {
        setClasses(response.data || []);
      } else {
        const errorMsg = response.error || 'Failed to fetch classes';
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Error fetching classes:', err);
      const errorMsg = err.message || 'Failed to fetch classes';
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch teachers from API
  const fetchTeachers = useCallback(async () => {
    try {
      setTeachersLoading(true);
      setTeachersError(null);
      const response = await teacherService.getAll();
      
      if (response.success) {
        setTeachers(response.data || []);
      } else {
        const errorMsg = response.error || 'Failed to fetch teachers';
        setTeachersError(errorMsg);
      }
    } catch (err) {
      console.error('Error fetching teachers:', err);
      const errorMsg = err.message || 'Failed to fetch teachers';
      setTeachersError(errorMsg);
    } finally {
      setTeachersLoading(false);
    }
  }, []);

  // Check roll number order for all classes
  const checkRollOrder = useCallback(async (classId) => {
    try {
      setRollOrderStatus(prev => ({ ...prev, [classId]: { checking: true } }));
      const response = await classService.checkRollOrder(classId);
      if (response.success) {
        setRollOrderStatus(prev => ({
          ...prev,
          [classId]: { needsReorder: response.data.needs_reorder, checking: false }
        }));
      }
    } catch (err) {
      console.error('Error checking roll order:', err);
      setRollOrderStatus(prev => ({ ...prev, [classId]: { needsReorder: false, checking: false } }));
    }
  }, []);

  // Check roll order for all classes when classes are loaded
  useEffect(() => {
    if (classes.length > 0) {
      classes.forEach(cls => {
        checkRollOrder(cls.id);
      });
    }
  }, [classes, checkRollOrder]);

  // Load data on component mount
  useEffect(() => {
    fetchClasses();
    fetchTeachers();
  }, [fetchClasses, fetchTeachers]);

  const handleAdd = async () => {
    setEditingClass(null);
    setModalOpen(true);
  };

  const handleEdit = async (classItem) => {
    setEditingClass(classItem);
    setModalOpen(true);
  };

  // Modal handlers
  const handleModalClose = () => {
    setModalOpen(false);
    setEditingClass(null);
    setFormError(null);
  };

  const handleModalSave = useCallback(async (formData) => {
    try {
      setModalLoading(true);
      setError(null);
      setFormError(null);

      const payload = {
        name: formData.name?.trim(),
        section: formData.section?.trim(),
        head_teacher_id: formData.head_teacher_id || null,
        order_number: Number(formData.order_number),
      };
      
      let response;
      if (editingClass) {
        // Update existing class
        response = await classService.update(editingClass.id, payload);
      } else {
        // Create new class
        response = await classService.create(payload);
      }
      
      if (response && !response.success) {
        const errorMsg = response.error || 'Failed to save class';
        showToast.error(errorMsg);
        // Set form error to display in the form
        if (errorMsg.toLowerCase().includes('head teacher') || errorMsg.toLowerCase().includes('teacher')) {
          setFormError(errorMsg);
        }
        // Don't close modal on error, keep it open so user can fix the issue
        return;
      }
      
      showToast.success(`Class "${formData.name}" ${editingClass ? 'updated' : 'created'} successfully`);
      
      // Refresh the classes list after successful create/update
      await fetchClasses();
      
      // Close modal
      handleModalClose();
    } catch (err) {
      console.error('Error saving class:', err);
      const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message || 'Failed to save class';
      showToast.error(errorMsg);
      // Set form error to display in the form
      if (errorMsg.toLowerCase().includes('head teacher') || errorMsg.toLowerCase().includes('teacher')) {
        setFormError(errorMsg);
      }
      // Don't close modal on error, keep it open so user can fix the issue
    } finally {
      setModalLoading(false);
    }
  }, [editingClass, fetchClasses]);

  const filteredClasses = useMemo(() => {
    if (!searchText.trim()) return classes;
    const query = searchText.toLowerCase();
    return classes.filter((cls) => {
      const values = [
        cls.name,
        cls.section,
        cls.head_teacher_name,
        cls.head_teacher_email,
        cls.head_teacher_phone,
        cls.order_number !== null && cls.order_number !== undefined ? String(cls.order_number) : null,
      ];
      return values.some((value) => value && value.toString().toLowerCase().includes(query));
    });
  }, [classes, searchText]);

  const sortedClasses = useMemo(() => {
    return [...filteredClasses].sort((a, b) => {
      const orderA = typeof a.order_number === 'number' ? a.order_number : Number.MAX_SAFE_INTEGER;
      const orderB = typeof b.order_number === 'number' ? b.order_number : Number.MAX_SAFE_INTEGER;
      if (orderA !== orderB) return orderA - orderB;
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      if (nameA !== nameB) return nameA.localeCompare(nameB);
      const sectionA = (a.section || '').toLowerCase();
      const sectionB = (b.section || '').toLowerCase();
      return sectionA.localeCompare(sectionB);
    });
  }, [filteredClasses]);

  const formatClassLabel = (name = '') => {
    if (!name) return 'Unnamed Class';
    return name.toLowerCase().startsWith('class ') ? name : `Class ${name}`;
  };

  const handleReorderRollNumbersClick = useCallback((classId) => {
    setReorderConfirmDialog({
      open: true,
      classId: classId,
    });
  }, []);

  const handleReorderRollNumbers = useCallback(async (classId) => {
    try {
      setRollOrderStatus(prev => ({ ...prev, [classId]: { reordering: true } }));
      const response = await classService.reorderRollNumbers(classId);
      
      if (response.success) {
        showToast.success(response.message || 'Roll numbers reordered successfully');
        // Re-check the order status
        await checkRollOrder(classId);
        setReorderConfirmDialog({ open: false, classId: null });
      } else {
        showToast.error(response.error || 'Failed to reorder roll numbers');
        setReorderConfirmDialog({ open: false, classId: null });
      }
    } catch (err) {
      console.error('Error reordering roll numbers:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to reorder roll numbers';
      showToast.error(errorMsg);
      setReorderConfirmDialog({ open: false, classId: null });
    } finally {
      setRollOrderStatus(prev => ({ ...prev, [classId]: { ...prev[classId], reordering: false } }));
    }
  }, [checkRollOrder]);

  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <Sidebar 
          open={sidebarOpen} 
          onClose={() => setSidebarOpen(false)} 
          drawerWidth={drawerWidth} 
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            ml: { xs: 0, md: `${0}px` },
            backgroundColor: 'background.default',
          }}
        >
          <Header onMenuClick={handleDrawerToggle} />
          <Box
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              mt: '64px',
              minHeight: 'calc(100vh - 64px)',
            }}
          >
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2, sm: 3 },
                borderRadius: 3,
                border: 1,
                borderColor: 'divider',
                mb: 3,
              }}
            >
              <Stack spacing={2}>
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    Classes Management
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Organize classes, assign head teachers, and control promotion order numbers.
                  </Typography>
                </Box>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
                  <TextField
                    fullWidth
                    placeholder="Search classes by name, section, teacher, or order number"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                      sx: { height: 40 },
                    }}
                    inputProps={{ sx: { py: 0, height: 40 } }}
                  />
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={{ width: { xs: '100%', md: '150px' }, height: 40, minHeight: 40, px: 2 }}
                    onClick={handleAdd}
                  >
                    Add Class
                  </Button>
                </Stack>
              </Stack>
            </Paper>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress />
              </Box>
            ) : sortedClasses.length === 0 ? (
              <Paper
                variant="outlined"
                sx={{
                  p: 3,
                  borderRadius: 3,
                  textAlign: 'center',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {searchText.trim() ? 'No classes match your search.' : 'No classes found. Click "Add Class" to create one.'}
                </Typography>
              </Paper>
            ) : (
              <Box
                sx={{
                  display: 'grid',
                  gap: { xs: 1.5, sm: 2 },
                  gridTemplateColumns: '1fr',
                }}
              >
                {sortedClasses.map((classItem, index) => (
                  <Paper
                    key={classItem.id}
                    variant="outlined"
                    sx={{
                      p: { xs: 1.5, sm: 2 },
                      borderRadius: 2,
                      borderColor: 'divider',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1.5,
                      width: '100%',
                      height: '100%',
                    }}
                  >
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'flex-start' }} justifyContent="space-between" flexWrap="nowrap">
                      {/* Left: Icon + Details */}
                      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          component="img"
                          src="/icons/classroom.png"
                          alt="Classroom"
                          sx={{
                            width: 48,
                            height: 48,
                            flexShrink: 0,
                            color: 'primary.main',
                          }}
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                            sx={{ mb: 0.5 }}
                          >
                            <Typography variant="subtitle1" fontWeight={700} noWrap>
                              {formatClassLabel(classItem.name)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" noWrap>
                              Section: {classItem.section || 'N/A'}
                            </Typography>
                          </Stack>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                          >
                            <Typography variant="body2" fontWeight={600}>
                              Head Teacher:
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {classItem.head_teacher_name || 'Not Assigned'}
                            </Typography>
                            {classItem.head_teacher_email && (
                              <Chip size="small" label={classItem.head_teacher_email} variant="outlined" />
                            )}
                            {classItem.head_teacher_phone && (
                              <Chip size="small" label={classItem.head_teacher_phone} variant="outlined" />
                            )}
                          </Stack>
                        </Box>
                      </Stack>

                      {/* Right: Actions */}
                      <Stack 
                        direction={{ xs: 'row', sm: 'column' }} 
                        spacing={1} 
                        alignItems={{ xs: 'center', sm: 'flex-end' }}
                        justifyContent={{ xs: 'flex-end', sm: 'flex-start' }}
                        sx={{ 
                          flex: '0 0 auto', 
                          minWidth: { xs: 'auto', sm: '100px' }
                        }}
                      >
                        <Chip
                          size="small"
                          color="primary"
                          variant="outlined"
                          label={`Order #${classItem.order_number ?? '—'}`}
                        />
                        {rollOrderStatus[classItem.id]?.needsReorder && (
                          <Button 
                            size="small" 
                            variant="outlined"
                            color="warning"
                            startIcon={<SortIcon />}
                            onClick={() => handleReorderRollNumbersClick(classItem.id)}
                            disabled={rollOrderStatus[classItem.id]?.reordering || rollOrderStatus[classItem.id]?.checking}
                            sx={{ 
                              minWidth: { xs: 'auto', sm: 'auto' },
                              flex: { xs: 1, sm: 'none' }
                            }}
                          >
                            {rollOrderStatus[classItem.id]?.reordering ? 'Reordering...' : 'Reorder Roll No.'}
                          </Button>
                        )}
                        <Button 
                          size="small" 
                          variant="outlined"
                          startIcon={<EditIcon />} 
                          onClick={() => handleEdit(classItem)}
                          sx={{ 
                            minWidth: { xs: 'auto', sm: 'auto' },
                            flex: { xs: 1, sm: 'none' }
                          }}
                        >
                          Edit
                        </Button>
                      </Stack>
                    </Stack>
                  </Paper>
                ))}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Class Form Modal */}
      <ClassForm
        open={modalOpen}
        onClose={handleModalClose}
        initialData={editingClass}
        onSubmit={handleModalSave}
        loading={modalLoading || teachersLoading}
        teachers={teachers}
        teachersError={teachersError || formError}
        title={editingClass ? 'Edit Class' : 'Add New Class'}
        onErrorClear={() => setFormError(null)}
      />

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={reorderConfirmDialog.open}
        onClose={() => setReorderConfirmDialog({ open: false, classId: null })}
        onConfirm={() => handleReorderRollNumbers(reorderConfirmDialog.classId)}
        title="Reorder Roll Numbers"
        message="Are you sure you want to reorder roll numbers alphabetically? This will update all students in this class."
        confirmText="Reorder"
        cancelText="Cancel"
        confirmColor="primary"
        severity="warning"
      />
    </>
  );
};

export default Classes;

